/* ============================================================
   The Cabretta PDP — Variant Picker + Gallery
   ============================================================ */

class FundayVariantPicker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const variantsScript = this.querySelector('[data-funday-variants]');
    if (!variantsScript) return;

    this.variants = JSON.parse(variantsScript.textContent);
    this.url = this.dataset.url || '';
    this.mostPopular = this.dataset.mostPopular || '';
    this.bestValue = this.dataset.bestValue || '';
    this.moneyFormat = window.Shopify && window.Shopify.currency
      ? window.Shopify.currency.active
      : 'USD';

    this.selected = this.getInitialSelection();
    this.computePackSummaries();
    this.bindEvents();
    this.update();
  }

  getInitialSelection() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    let variant = null;
    if (variantId) {
      variant = this.variants.find(v => String(v.id) === String(variantId));
    }
    if (!variant && this.mostPopular) {
      variant = this.variants.find(v => v.option1 === this.mostPopular && v.available);
    }
    if (!variant) {
      variant = this.variants.find(v => v.available) || this.variants[0];
    }

    return [variant.option1, variant.option2, variant.option3];
  }

  bindEvents() {
    this.querySelectorAll('[data-funday-option-value]').forEach(btn => {
      btn.addEventListener('click', () => {
        const index = parseInt(btn.dataset.fundayOptionIndex, 10);
        const value = btn.dataset.fundayOptionValue;
        this.selected[index] = value;
        this.update();
      });
    });
  }

  findVariant() {
    return this.variants.find(v =>
      v.option1 === this.selected[0] &&
      (this.selected[1] == null || v.option2 === this.selected[1]) &&
      (this.selected[2] == null || v.option3 === this.selected[2])
    );
  }

  glovesInPack(packValue) {
    if (!packValue) return 1;
    if (/single/i.test(packValue)) return 1;
    const match = packValue.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  }

  computePackSummaries() {
    const packGroup = this.querySelector('[data-funday-option-group="0"]');
    if (!packGroup) return;

    const packBtns = packGroup.querySelectorAll('[data-funday-option-value]');
    const packValues = Array.from(packBtns).map(b => b.dataset.fundayOptionValue);

    // Find single-glove pack as savings baseline
    let singlePerGlove = null;
    const singlePackValue = packValues.find(v => this.glovesInPack(v) === 1);
    if (singlePackValue) {
      const singleVariant = this.variants.find(v => v.option1 === singlePackValue && v.available);
      if (singleVariant) singlePerGlove = singleVariant.price;
    }

    packValues.forEach(packValue => {
      const variant = this.variants.find(v => v.option1 === packValue && v.available)
        || this.variants.find(v => v.option1 === packValue);
      if (!variant) return;

      const gloveCount = this.glovesInPack(packValue);
      const perGlove = Math.round(variant.price / gloveCount);
      const totalIfBoughtSingle = singlePerGlove ? singlePerGlove * gloveCount : null;
      const savings = totalIfBoughtSingle ? totalIfBoughtSingle - variant.price : 0;

      const priceEl = this.querySelector(`[data-funday-pack-price="${CSS.escape(packValue)}"]`);
      const subEl = this.querySelector(`[data-funday-pack-sub="${CSS.escape(packValue)}"]`);

      if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
      if (subEl) {
        let subText = `${this.formatMoney(perGlove)} / glove`;
        if (savings > 0 && gloveCount > 1) {
          subText += ` · save ${this.formatMoney(savings)}`;
        }
        subEl.textContent = subText;
      }
    });
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }
    const dollars = cents / 100;
    const hasCents = dollars % 1 !== 0;
    return '$' + dollars.toFixed(hasCents ? 2 : 0);
  }

  update() {
    // Visual selection state
    this.querySelectorAll('[data-funday-option-value]').forEach(btn => {
      const index = parseInt(btn.dataset.fundayOptionIndex, 10);
      const value = btn.dataset.fundayOptionValue;
      btn.classList.toggle('is-selected', this.selected[index] === value);
    });

    // Mark unavailable variants on each chip
    this.markUnavailableOptions();

    const variant = this.findVariant();

    // Update hidden form input
    const variantIdInput = this.querySelector('[data-funday-variant-id]');
    if (variantIdInput && variant) variantIdInput.value = variant.id;

    // Update CTA
    const ctaBtn = this.querySelector('[data-funday-cta]');
    const ctaText = this.querySelector('[data-funday-cta-text]');

    if (!variant) {
      if (ctaBtn) ctaBtn.disabled = true;
      if (ctaText) ctaText.textContent = 'Unavailable';
    } else if (!variant.available) {
      if (ctaBtn) ctaBtn.disabled = true;
      if (ctaText) ctaText.textContent = 'Sold out';
    } else {
      if (ctaBtn) ctaBtn.disabled = false;
      const packName = variant.option1 || '';
      const priceFormatted = this.formatMoney(variant.price);
      if (ctaText) {
        // "Add 3-pack — $75" pattern. For Single, just "Add to cart — $X"
        if (packName && !/single/i.test(packName)) {
          ctaText.textContent = `Add ${packName} — ${priceFormatted}`;
        } else {
          ctaText.textContent = `Add to cart — ${priceFormatted}`;
        }
      }
    }

    // Update URL without reload
    if (variant) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  markUnavailableOptions() {
    // For each chip, check if there's any variant that matches the current
    // selection in OTHER positions AND has this option value at this position
    // AND is available. If not, mark unavailable.
    this.querySelectorAll('[data-funday-option-value]').forEach(btn => {
      const index = parseInt(btn.dataset.fundayOptionIndex, 10);
      const value = btn.dataset.fundayOptionValue;

      const matches = this.variants.filter(v => {
        if (index === 0 && v.option1 !== value) return false;
        if (index === 1 && v.option2 !== value) return false;
        if (index === 2 && v.option3 !== value) return false;
        // Match other dimensions to current selection
        if (index !== 0 && this.selected[0] && v.option1 !== this.selected[0]) return false;
        if (index !== 1 && this.selected[1] && v.option2 !== this.selected[1]) return false;
        if (index !== 2 && this.selected[2] && v.option3 !== this.selected[2]) return false;
        return true;
      });

      const anyAvailable = matches.some(v => v.available);
      btn.classList.toggle('is-unavailable', !anyAvailable);
    });
  }
}

if (!customElements.get('funday-variant-picker')) {
  customElements.define('funday-variant-picker', FundayVariantPicker);
}

/* ----- Size guide modal ----- */
document.addEventListener('click', function (e) {
  // Open modal
  const opener = e.target.closest('[data-funday-modal-open]');
  if (opener) {
    e.preventDefault();
    const id = opener.dataset.fundayModalOpen;
    const dialog = document.getElementById(id);
    if (dialog && typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else if (dialog) {
      dialog.setAttribute('open', '');
    }
    return;
  }

  // Close button inside modal
  const closer = e.target.closest('[data-funday-modal-close]');
  if (closer) {
    const dialog = closer.closest('dialog');
    if (dialog) {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }
    return;
  }

  // Backdrop click — close when clicking the dialog element directly
  if (e.target.tagName === 'DIALOG' && e.target.classList.contains('funday-modal')) {
    if (typeof e.target.close === 'function') {
      e.target.close();
    }
  }
});

/* ----- Gallery thumb switcher ----- */
document.addEventListener('click', function (e) {
  const thumb = e.target.closest('[data-funday-thumb]');
  if (!thumb) return;

  const section = thumb.closest('.funday-pdp__container') || document;
  const allThumbs = section.querySelectorAll('[data-funday-thumb]');
  allThumbs.forEach(t => t.classList.remove('is-active'));
  thumb.classList.add('is-active');

  const mainContainer = section.querySelector('[data-funday-main-media]');
  if (!mainContainer) return;

  const mainImg = mainContainer.querySelector('img');
  const fullSrc = thumb.dataset.fullSrc;
  if (mainImg && fullSrc) {
    mainImg.src = fullSrc;
    mainImg.srcset = '';
  } else if (mainImg) {
    // Fallback: upscale the thumb src
    const thumbImg = thumb.querySelector('img');
    if (thumbImg) {
      mainImg.src = thumbImg.src.replace(/width=\d+/, 'width=800');
      mainImg.srcset = '';
    }
  }
});

/* ============================================================
   Waitlist — Klaviyo subscribe form
   ============================================================ */
class FundayKlaviyoForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.publicKey = this.dataset.publicKey;
    this.listId = this.dataset.listId;
    this.successMessage = this.dataset.success || "You're on the list.";
    this.errorMessage = this.dataset.error || 'Something went wrong. Please try again.';

    this.form = this.querySelector('[data-funday-klaviyo-form]');
    this.input = this.querySelector('[data-funday-email-input]');
    this.submit = this.querySelector('[data-funday-submit]');
    this.submitText = this.querySelector('[data-funday-submit-text]');
    this.message = this.querySelector('[data-funday-message]');
    this.microcopy = this.querySelector('[data-funday-microcopy]');

    if (!this.form) return;
    this.originalSubmitText = this.submitText ? this.submitText.textContent : '';
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  async handleSubmit(e) {
    e.preventDefault();

    const email = (this.input.value || '').trim();
    if (!email || !this.isValidEmail(email)) {
      this.showMessage(this.errorMessage, 'error');
      return;
    }

    if (!this.publicKey || !this.listId) {
      console.error('FundayKlaviyoForm: missing data-public-key or data-list-id.');
      this.showMessage(this.errorMessage, 'error');
      return;
    }

    this.setLoading(true);

    try {
      const response = await fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(this.publicKey),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15',
            'accept': 'application/json'
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Glove Club Waitlist',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email
                    }
                  }
                }
              },
              relationships: {
                list: { data: { type: 'list', id: this.listId } }
              }
            }
          })
        }
      );

      // Klaviyo returns 202 Accepted on success
      if (response.status === 202 || response.ok) {
        this.showMessage(this.successMessage, 'success');
        this.input.value = '';
        if (this.form) this.form.style.display = 'none';
        if (this.microcopy) this.microcopy.style.display = 'none';

        // Fire Meta Pixel Lead event for ad optimization
        if (typeof fbq !== 'undefined') {
          fbq('track', 'Lead', {
            content_name: 'Glove Club Waitlist',
            content_category: 'waitlist_signup'
          });
        }

        // Fire Google Analytics / GTM event if dataLayer present
        if (typeof window.dataLayer !== 'undefined') {
          window.dataLayer.push({
            event: 'waitlist_signup',
            list_name: 'Glove Club Waitlist'
          });
        }
      } else {
        const text = await response.text();
        console.error('Klaviyo error response:', response.status, text);
        this.showMessage(this.errorMessage, 'error');
      }
    } catch (err) {
      console.error('Klaviyo fetch error:', err);
      this.showMessage(this.errorMessage, 'error');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (this.submit) this.submit.disabled = loading;
    if (this.submitText) {
      this.submitText.textContent = loading ? 'Saving…' : this.originalSubmitText;
    }
  }

  showMessage(text, type) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.className = 'funday-fm-card__message is-' + type;
    this.message.hidden = false;
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}

if (!customElements.get('funday-klaviyo-form')) {
  customElements.define('funday-klaviyo-form', FundayKlaviyoForm);
}